import React from 'react'
import PropTypes from 'prop-types'
import { Form, Button } from 'react-bootstrap'
import { Formik } from 'formik'
import * as yup from 'yup'

import UserRegisterModal from 'shared/components/modals/UserRegisterModal'

import BaseLink from 'shared/components/BaseLink'
import { loginUser, completeLoginWithTokens } from 'Store/Auth/actions'
import { MFA_LOGIN_STEP } from 'Store/Auth/actions/mfaActions'
import FormError from 'shared/components/FormError'
import SpinnerLoader from 'shared/components/Loaders/SpinnerLoader'
import MfaLoginVerify from 'shared/components/forms/LoginForm/MfaLoginVerify'
import MfaLoginEnroll from 'shared/components/forms/LoginForm/MfaLoginEnroll'
import './style.scss'

const schema = yup.object({
  username: yup.string().required().label('Username or Email'),
  password: yup.string().required().label('Password'),
})

const INITIAL_MFA = {
  step: 'password',
  challengeId: null,
  methods: [],
  enrollmentToken: null,
  enrollDetail: null,
}

class LoginForm extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      validated: false,
      mfa: INITIAL_MFA,
    }

    this.handleSubmit = this.handleSubmit.bind(this)
    this.openSignUp = this.openSignUp.bind(this)
    this.handleMfaTokens = this.handleMfaTokens.bind(this)
    this.resetMfa = this.resetMfa.bind(this)
    this.handleEnrollComplete = this.handleEnrollComplete.bind(this)
  }

  resetMfa() {
    this.setState({ mfa: INITIAL_MFA })
  }

  handleMfaTokens(tokenData) {
    this.props.dispatch(completeLoginWithTokens(tokenData, this.props.postLoginAction))
  }

  applyLoginStep(result) {
    if (!result || !result.step) return
    if (result.step === MFA_LOGIN_STEP.MFA_VERIFY) {
      this.setState({
        mfa: {
          step: 'verify',
          challengeId: result.challengeId,
          methods: result.methods,
          enrollmentToken: null,
          enrollDetail: null,
        },
      })
    } else if (result.step === MFA_LOGIN_STEP.MFA_ENROLL) {
      this.setState({
        mfa: {
          step: 'enroll',
          challengeId: null,
          methods: [],
          enrollmentToken: result.enrollmentToken,
          enrollDetail: result.detail,
        },
      })
    }
  }

  handleEnrollComplete() {
    if (!this.lastCredentials) {
      this.resetMfa()
      return
    }
    this.props
      .dispatch(loginUser(this.lastCredentials, this.props.postLoginAction))
      .then(result => this.applyLoginStep(result))
  }

  handleSubmit(formData) {
    this.setState({ validated: true })
    this.lastCredentials = { username: formData.username, password: formData.password }
    const userData = { username: formData.username, password: formData.password }
    this.props
      .dispatch(loginUser(userData, this.props.postLoginAction))
      .then(result => this.applyLoginStep(result))
  }

  openSignUp(e) {
    e.preventDefault()
    this.props.modal.setModal({
      modalComponent: UserRegisterModal,
      modalProps: {
        size: 'lg',
      },
    })
  }

  render() {
    const { mfa } = this.state

    if (mfa.step === 'verify') {
      return (
        <MfaLoginVerify
          challengeId={mfa.challengeId}
          methods={mfa.methods}
          onSuccess={this.handleMfaTokens}
          onBack={this.resetMfa}
        />
      )
    }

    if (mfa.step === 'enroll') {
      return (
        <MfaLoginEnroll
          enrollmentToken={mfa.enrollmentToken}
          detail={mfa.enrollDetail}
          onSuccess={this.handleMfaTokens}
          onEnrollmentComplete={this.handleEnrollComplete}
          onBack={this.resetMfa}
        />
      )
    }

    return (
      <Formik onSubmit={e => this.handleSubmit(e)} validationSchema={schema} initialValues={{ username: '', password: '' }}>
        {({ handleSubmit, handleChange, handleBlur, touched, errors, submitCount }) => {
          return (
            <Form
              noValidate
              className="login-form auth-login-form"
              validated={this.state.validated}
              onSubmit={handleSubmit}
            >
              <p className="login-form__text mb-3">
                Log in to your account to bookmark properties, save custom searches, and set up notifications for new
                data results.
              </p>
              <p className="login-form__text mb-3">
                If you do not have an account yet,
                <a onClick={e => this.openSignUp(e)}> sign up </a>
                for one now.
              </p>
              <FormError show={!!this.props.error && this.props.error.status !== 500} message={(this.props.error || {}).message} />
              <Form.Group controlId="loginUsername">
                <Form.Label>Username or Email</Form.Label>
                <Form.Control
                  required
                  name="username"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={!!this.props.submitCount || (touched.username && errors.username)}
                  type="text"
                  placeholder=""
                />
                <FormError show={!!((submitCount || touched.username) && errors.username)} message={errors.username} />
              </Form.Group>

              <Form.Group controlId="loginPassword">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  required
                  name="password"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={!!this.props.submitCount || (touched.password && errors.password)}
                  placeholder=""
                  type="password"
                />
                <FormError show={!!((submitCount || touched.password) && errors.password)} message={errors.password} />
              </Form.Group>
              <div className="login-form__submit-row">
                <BaseLink className="btn-link" href="https://api.displacementalert.org/password_reset">
                  Reset Password
                </BaseLink>
                <Button
                  className="login-form__submit-button btn-loader"
                  disabled={this.props.loading}
                  variant="dark"
                  type="submit"
                  size="md"
                >
                  <span>Submit</span>
                  <div className="button-loader__container">{this.props.loading && <SpinnerLoader size="20px" />}</div>
                </Button>
              </div>
            </Form>
          )
        }}
      </Formik>
    )
  }
}

LoginForm.propTypes = {
  dispatch: PropTypes.func,
  modal: PropTypes.object,
  error: PropTypes.object,
  loading: PropTypes.bool,
}

export default LoginForm
